import React from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { Edit, Trash2, Eye, RotateCcw } from 'lucide-react';
import { Customer } from '../types';

interface CustomersTableProps {
  customers: Customer[];
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onViewDetails: (customer: Customer) => void;
  onReactivate?: (customer: Customer) => void;
}

type SortField = 'name' | 'document' | 'email' | 'loyaltyPoints' | 'isActive';

export const CustomersTable: React.FC<CustomersTableProps> = ({
  customers,
  onEdit,
  onDelete,
  onViewDetails,
  onReactivate,
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    name: 'Cliente',
    document: 'Documento',
    email: 'Contacto',
    loyaltyPoints: 'Puntos',
    isActive: 'Estado'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (customer: Customer, field: SortField): any => {
    switch (field) {
      case 'name':
        return customer.name.toLowerCase();
      case 'document':
        return customer.document;
      case 'email':
        return customer.email.toLowerCase();
      case 'loyaltyPoints':
        return customer.loyaltyPoints;
      case 'isActive':
        return customer.isActive ? 1 : 0;
      default:
        return customer.name;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Customer, SortField>(
    {
      data: customers,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 12,
      sortValueExtractor
    },
    fieldLabels
  );
  if (customers.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No hay clientes registrados</p>
      </div>
    );
  }

  const getTagColor = (tag: string) => {
    switch (tag.toLowerCase()) {
      case 'vip':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'frecuente':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'nuevo':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'inactivo':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left py-3 px-4"
              >
                Cliente
              </SortableHeader>
              <SortableHeader
                field="document"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left py-3 px-4"
              >
                Documento
              </SortableHeader>
              <SortableHeader
                field="email"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left py-3 px-4"
              >
                Contacto
              </SortableHeader>
              {/* Puntos de Lealtad - Oculto temporalmente
              <SortableHeader
                field="loyaltyPoints"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left py-3 px-4"
              >
                Puntos
              </SortableHeader>
              */}
              <SortableHeader
                field="isActive"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left py-3 px-4"
              >
                Estado
              </SortableHeader>
              <th className="text-left py-3 px-4">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {tableData.pagination.paginatedData.map((customer) => (
              <tr key={customer.id} className="border-b hover:bg-muted/50">
                <td className="py-3 px-4">
                  <div>
                    <div className="font-medium">{customer.name}</div>
                    <div className="flex gap-1 mt-1">
                      {customer.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className={getTagColor(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm">
                    {customer.documentType}: {customer.document}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm">
                    <div>{customer.email}</div>
                    <div className="text-muted-foreground">{customer.phone}</div>
                  </div>
                </td>
                {/* Puntos de Lealtad - Oculto temporalmente
                <td className="py-3 px-4">
                  <div className="font-medium">{customer.loyaltyPoints}</div>
                </td>
                */}
                <td className="py-3 px-4">
                  <Badge
                    variant={customer.isActive ? "default" : "secondary"}
                    className={customer.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
                  >
                    {customer.isActive ? "Activo" : "Inactivo"}
                  </Badge>
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(customer)}
                      aria-label={`Ver detalles de ${customer.name}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {customer.isActive && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(customer)}
                          aria-label={`Editar ${customer.name}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(customer)}
                          aria-label={`Eliminar ${customer.name}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {!customer.isActive && onReactivate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onReactivate(customer)}
                        aria-label={`Reactivar ${customer.name}`}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};